/**
 * 用户业务类型定义
 * 包含用户、认证、套餐等相关类型
 */

import type { PaymentMethod } from './apiTypes';

// ============= 基础类型 =============

/**
 * 基础实体接口
 */
export interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * 状态类型
 */
export type Status = 'active' | 'inactive' | 'pending' | 'suspended';

// ============= 用户基础类型 =============

/**
 * 用户使用量信息
 */
export interface UserUsage {
  used: number;
  limit: number;
  remaining?: number;
  resetDate?: string;
}

/**
 * 用户接口
 */
export interface User extends BaseEntity {
  username: string;
  email: string;
  avatar?: string;
  phone?: string;
  introduction?: string;
  charCount?: number;
  role?: string;
  status?: string;
  hasPassword?: boolean; // 是否已设置密码
  usage?: UserUsage; // ✅ 新增用户使用量信息
}

// ============= 认证相关类型 =============

/**
 * 登录表单
 */
export interface LoginForm {
  email: string;
  password: string;
}

/**
 * 注册表单
 */
export interface RegisterForm {
  username: string;
  email: string;
  password: string;
  verificationCode: string;
}

// LoginResponse 已在 apiTypes.ts 中定义

// ============= 套餐相关类型 =============

/**
 * 套餐类型
 */
export type PackageType = 'free' | 'basic' | 'premium' | 'enterprise';

/**
 * 套餐状态
 */
export type PackageStatus = 'active' | 'expired' | 'cancelled';

/**
 * 套餐接口
 */
export interface Package extends BaseEntity {
  code: string;
  description: string;
  versionCode: string;    // basic/pro/enterprise
  versionName: string;    // 版本名称
  versionTagline: string; // 版本标语
  versionSort: number;    // 版本排序
  displayName: string;    // 显示名称：1个月/3个月/12个月
  months: number;         // 月数
  price: number;          // 总价格，单位分
  pricePerMonth: number;  // 单月折算价，单位分
  duration: number;       // 天数
  charLimit: number;      // 字符限制
  features: string[];     // 功能列表
  status: Status;
  isRecommended: boolean; // 是否推荐
}

// UserPackage 类型已移至 @/api/package.ts 中统一定义

// ============= 订单相关类型 =============

/**
 * 订单状态
 */
export type OrderStatus = 'pending' | 'paid' | 'cancelled' | 'refunded';

// PaymentMethod 已在 apiTypes.ts 中定义

/**
 * 订单接口
 */
export interface Order extends BaseEntity {
  userId: string;
  packageId: string;
  package: Package;
  amount: number;
  currency: string;
  status: OrderStatus;
  paymentMethod?: PaymentMethod;
  paymentId?: string;
  paidAt?: string;
  expiredAt?: string;
}

/**
 * 创建订单参数
 */
export interface CreateOrderParams {
  packageId: string;
  paymentMethod: PaymentMethod;
  returnUrl?: string;
}

// ============= 用户设置类型 =============

/**
 * 用户偏好设置
 */
export interface UserPreferences {
  language: string;
  theme: 'light' | 'dark' | 'auto';
  notifications: {
    email: boolean;
    push: boolean;
    sms: boolean;
  };
  privacy: {
    profileVisible: boolean;
    activityVisible: boolean;
  };
  tts: {
    defaultVoice: string;
    defaultSpeed: number;
    defaultFormat: string;
    autoPlay: boolean;
  };
  library: {
    defaultDownloadTTL: number; // 默认下载链接有效期（秒）
    defaultStreamTTL: number;   // 默认播放链接有效期（秒）
    autoRenewThreshold: number; // 自动续期阈值（秒）
  };
}

/**
 * 用户配置文件
 */
export interface UserProfile extends BaseEntity {
  userId: string;
  nickname?: string;
  bio?: string;
  location?: string;
  website?: string;
  phone?: string;
  preferences: UserPreferences;
}

// ============= 用户统计类型 =============

/**
 * 用户使用统计
 */
export interface UserStats {
  totalTasks: number;
  successfulTasks: number;
  failedTasks: number;
  totalCharacters: number;
  remainingCharacters: number;
  totalDuration: number;
  averageTaskTime: number;
  mostUsedVoice: string;
  dailyUsage: Array<{
    date: string;
    tasks: number;
    characters: number;
  }>;
  monthlyUsage: Array<{
    month: string;
    tasks: number;
    characters: number;
  }>;
}

/**
 * 用户活动记录
 */
export interface UserActivity extends BaseEntity {
  userId: string;
  type: 'login' | 'logout' | 'tts_convert' | 'package_purchase' | 'profile_update';
  description: string;
  metadata?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
}

// ============= 会员相关类型 =============

/**
 * 会员等级
 */
export type MembershipLevel = 'bronze' | 'silver' | 'gold' | 'platinum' | 'diamond';

/**
 * 会员信息
 */
export interface Membership extends BaseEntity {
  userId: string;
  level: MembershipLevel;
  points: number;
  benefits: string[];
  nextLevelPoints: number;
  expiredAt?: string;
}

/**
 * 积分记录
 */
export interface PointsRecord extends BaseEntity {
  userId: string;
  type: 'earn' | 'spend' | 'expire';
  amount: number;
  reason: string;
  balance: number;
  expiredAt?: string;
}

// ============= 邀请相关类型 =============

/**
 * 邀请状态
 */
export type InvitationStatus = 'pending' | 'accepted' | 'expired';

/**
 * 邀请记录
 */
export interface Invitation extends BaseEntity {
  inviterId: string;
  inviteeEmail: string;
  inviteeId?: string;
  code: string;
  status: InvitationStatus;
  reward: number;
  expiredAt: string;
  acceptedAt?: string;
}

// ============= 常量定义 =============

export const USER_ROLES = {
  ADMIN: 'admin',
  USER: 'user',
  VIP: 'vip'
} as const;

export const PACKAGE_TYPES: PackageType[] = ['free', 'basic', 'premium', 'enterprise'];

export const PAYMENT_METHODS: PaymentMethod[] = ['alipay', 'wechat', 'bank_card'];

export const MEMBERSHIP_LEVELS: MembershipLevel[] = ['bronze', 'silver', 'gold', 'platinum', 'diamond'];

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  language: 'zh-CN',
  theme: 'auto',
  notifications: {
    email: true,
    push: true,
    sms: false
  },
  privacy: {
    profileVisible: true,
    activityVisible: false
  },
  tts: {
    defaultVoice: 'zh-CN-XiaoxiaoNeural',
    defaultSpeed: 1.0,
    defaultFormat: 'mp3',
    autoPlay: false
  },
  library: {
    defaultDownloadTTL: 86400,  // 1天
    defaultStreamTTL: 900,       // 15分钟
    autoRenewThreshold: 60       // 1分钟
  }
};
